import {useMemo} from 'react';

import {
  appropriateTimestep,
  getUnitFromMaxValue,
  timestepToFactor,
} from '../../../util/plotHelpers/time';
import {
  type LineWithSource,
  type Timestep,
} from '../../../util/plotHelpers/types';

type BackendLinesDomain = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
} | null;

type UseTimeConvertedLinesParams = {
  /** Lines to convert - must have source property for correct mutation strategy */
  lines: LineWithSource[];
  /** Whether time conversion is needed (xAxis is _runtime or _absolute_runtime without expression) */
  needsTimeConversion: boolean;
  /** Whether all runsets have backend lines data */
  allRunsetsHaveBackendLines: boolean;
  /** Pre-computed domain from backend lines */
  backendLinesDomain: BackendLinesDomain;
  /** Locked timestep during zoom (from PanelTimeContext) */
  zoomTimestep: Timestep | null;
};

/**
 * Converts line x-values from seconds to appropriate time unit (minutes/hours/days).
 *
 * Uses source-aware strategy:
 * - Backend lines: map() to create new objects (defensive copy - cached data)
 * - Frontend lines: mutate in place (safe - parseHistory creates new objects)
 *
 * Timestep determination priority:
 * 1. zoomTimestep (locked during zoom)
 * 2. backendLinesDomain.xMax (fast path when all backend lines)
 * 3. appropriateTimestep() scan of all line points
 */
export function useTimeConvertedLines({
  lines,
  needsTimeConversion,
  allRunsetsHaveBackendLines,
  backendLinesDomain,
  zoomTimestep,
}: UseTimeConvertedLinesParams): LineWithSource[] {
  return useMemo(() => {
    if (!needsTimeConversion) {
      return lines;
    }

    // Determine unit: zoom lock > backend domain > scan points
    let unit: Timestep;
    if (zoomTimestep) {
      unit = zoomTimestep;
    } else if (allRunsetsHaveBackendLines && backendLinesDomain) {
      unit = getUnitFromMaxValue(backendLinesDomain.xMax);
    } else {
      unit = appropriateTimestep(lines);
    }

    const factor = timestepToFactor(unit);

    // No conversion needed for seconds, but still set timestep for axis labels
    if (factor === 1) {
      return lines.map(line => ({...line, timestep: unit}));
    }

    // Apply conversion based on line source
    return lines.map(line => {
      if (line.source === 'backend') {
        // Backend lines: create new point objects (defensive copy required)
        return {
          ...line,
          data: line.data.map(point => ({
            ...point,
            x: point.x / factor,
          })),
          timestep: unit,
          ...(line.nanPoints &&
            line.nanPoints.length > 0 && {
              nanPoints: line.nanPoints.map(point => ({
                ...point,
                x: point.x / factor,
              })),
            }),
          ...(line.domain && {
            domain: {
              ...line.domain,
              minX: line.domain.minX / factor,
              maxX: line.domain.maxX / factor,
            },
          }),
        };
      }
      // Frontend lines: mutate in place (safe - parseHistory creates new objects)
      line.data.forEach(point => {
        point.x = point.x / factor;
      });
      if (line.nanPoints) {
        line.nanPoints.forEach(point => {
          point.x = point.x / factor;
        });
      }
      if (line.domain) {
        line.domain.minX = line.domain.minX / factor;
        line.domain.maxX = line.domain.maxX / factor;
      }
      line.timestep = unit;
      return line;
    });
  }, [
    lines,
    needsTimeConversion,
    allRunsetsHaveBackendLines,
    backendLinesDomain,
    zoomTimestep,
  ]);
}
